import React, { FC, ReactNode, useEffect, useState, useCallback } from 'react';
import { useWindowWidth } from '@react-hook/window-size';
import * as Styled from './FixedTopbar.styles';

import { isSearchPage } from '../../utils/helpers';

export type FixedTopbarProps = {
  displayHeaderFilters?: boolean;
  children: ReactNode;
};

/**
 * FixedTopbar is a functional component that renders a topbar
 * with optional header filters.
 *
 * @param {object} props - The properties object.
 * @param {React.ReactNode} props.children - The child elements to be displayed within the topbar.
 * @param {boolean} [props.displayHeaderFilters=false] - Determines whether header filters should be displayed.
 * @returns {JSX.Element} The rendered topbar component.
 */
const FixedTopbar: FC<FixedTopbarProps> = ({
  children,
  displayHeaderFilters = false,
}) => {
  const [stickyTop, setStickyTop] = useState(0);
  const windowWidth = useWindowWidth();
  const shouldSticky =
    (displayHeaderFilters || isSearchPage()) && windowWidth > 1024;

  const calcTopPosition = useCallback(() => {
    const headerElement = document.querySelector('header.page-header');
    if (headerElement instanceof HTMLElement) {
      const headerHeight = headerElement.offsetHeight - 1;
      if (stickyTop !== headerHeight) {
        setStickyTop(headerHeight);
      }
    }
  }, [stickyTop]);

  useEffect(() => {
    calcTopPosition();
    window.addEventListener('scroll', calcTopPosition);
    return () => {
      window.removeEventListener('scroll', calcTopPosition);
    };
  }, [calcTopPosition]);

  return (
    <Styled.Root
      enableStickyMode={shouldSticky}
      style={{
        ...(shouldSticky && { top: stickyTop }),
      }}
    >
      <Styled.Wrapper>{children}</Styled.Wrapper>
    </Styled.Root>
  );
};

export default FixedTopbar;
